/**
 * EtatsDesLieuxListPanel — liste des états des lieux RÉELS (signalements marqués
 * est_etat_des_lieux) + bouton de création via EtatDesLieuxModal.
 *
 * Vit dans l'onglet « États des lieux » (au-dessus des templates). Les fiches
 * incident classiques restent dans l'onglet « Fiches incident » : la séparation
 * se fait par le filtre serveur estEtatDesLieux (mig existante, pas de nouvelle
 * migration). Un EDL suit le cycle allégé planifié → en cours → résolu.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { Button, Badge, Card } from '@/components/ui';
import { Plus, ClipboardList, ChevronRight, Inbox } from 'lucide-react';
import type { Signalement } from '@roronoa-collectivites/shared-types';
import { EtatDesLieuxModal } from '@/pages/service-technique/EtatDesLieuxModal';
import { statutBadge } from '@/pages/service-technique/statut-badge';

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

export function EtatsDesLieuxListPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Signalement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.serviceTechnique.signalements.list({
        estEtatDesLieux: true,
        pageSize: 100,
      });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">États des lieux réalisés</h2>
        <Button variant="primary" size="sm" onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Nouvel état des lieux</span>
        </Button>
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      {loading ? (
        <Card variant="glass" padded><p className="text-sm text-ink-muted">Chargement…</p></Card>
      ) : items.length === 0 ? (
        <Card variant="glass" padded>
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <Inbox className="h-8 w-8 text-ink-dim" aria-hidden="true" />
            <p className="text-sm text-ink-muted">Aucun état des lieux pour le moment.</p>
            <p className="text-xs text-ink-dim">
              Créez-en un depuis un modèle (les points à vérifier se pré-remplissent).
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((s) => {
            const badge = statutBadge(s);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => navigate(`/service-technique/signalements/${s.id}`)}
                className="flex w-full items-center gap-3 rounded-r-md border border-border bg-surface/50 px-4 py-3 text-left transition-colors hover:border-tech/40 hover:bg-tech-tint/20"
              >
                <ClipboardList className="h-5 w-5 shrink-0 text-tech-fg" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-2xs text-ink-dim">{s.numero}</span>
                    <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                  </div>
                  <p className="truncate text-sm font-medium text-ink">{s.sujet}</p>
                  <p className="truncate text-xs text-ink-subtle">
                    {formatDate(s.createdAt)}
                    {s.localisationAdresse ? ` · ${s.localisationAdresse}` : ''}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-dim" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <EtatDesLieuxModal
          onClose={() => setModalOpen(false)}
          onCreated={(id) => { setModalOpen(false); navigate(`/service-technique/signalements/${id}`); }}
          onExistant={(edlId) => { setModalOpen(false); navigate(`/service-technique/signalements/${edlId}`); }}
        />
      )}
    </div>
  );
}
